import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import type { FieldDefinitionRequest } from '../api';
import type { FieldDefinitionDraft } from '../hooks/useFieldDefinitionState';
import { FieldRow } from './FieldRow';

interface FieldListProps {
  fields: FieldDefinitionDraft[];
  setFields: (next: FieldDefinitionDraft[]) => void;
  onUpdate: (draftId: string, patch: Partial<Omit<FieldDefinitionRequest, 'children'>>) => void;
  onRemove: (draftId: string) => void;
  onAdd: () => void;
  onSetChildren: (draftId: string, children: FieldDefinitionDraft[] | null) => void;
  onChildAdd: (parentDraftId: string) => void;
  onChildRemove: (parentDraftId: string, childDraftId: string) => void;
  onChildUpdate: (
    parentDraftId: string,
    childDraftId: string,
    patch: Partial<Omit<FieldDefinitionRequest, 'children'>>,
  ) => void;
  /** Validation errors keyed by `draftId`. */
  errors?: Record<string, Partial<Record<keyof FieldDefinitionRequest, string>>>;
  disabled?: boolean;
}

/**
 * Sortable list of `FieldRow`s.
 * Reorders are committed locally; the parent persists them via the update mutation.
 */
export function FieldList({
  fields,
  setFields,
  onUpdate,
  onRemove,
  onAdd,
  onSetChildren,
  onChildAdd,
  onChildRemove,
  onChildUpdate,
  errors,
  disabled = false,
}: FieldListProps): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Touch drag needs a hold delay so the list can still scroll by swipe;
    // the drag handle additionally sets touch-none (see FieldRow).
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (over === null || active.id === over.id) {
      return;
    }
    const oldIndex = fields.findIndex((f) => f.draftId === active.id);
    const newIndex = fields.findIndex((f) => f.draftId === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    setFields(arrayMove(fields, oldIndex, newIndex));
  };

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={fields.map((f) => f.draftId)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {fields.map((field, index) => (
              <FieldRow
                key={field.draftId}
                field={field}
                index={index}
                onChange={onUpdate}
                onRemove={onRemove}
                onSetChildren={onSetChildren}
                onChildAdd={onChildAdd}
                onChildRemove={onChildRemove}
                onChildUpdate={onChildUpdate}
                {...(errors?.[field.draftId] !== undefined ? { errors: errors[field.draftId] } : {})}
                disabled={disabled}
              />
            ))}
            {fields.length === 0 ? (
              <div className="card p-6 text-center text-sm text-slate-500">
                Este tipo de documento aún no tiene campos. Añade el primero para empezar.
              </div>
            ) : null}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={onAdd}
        className="btn-secondary"
        disabled={disabled}
      >
        + Añadir campo
      </button>
    </div>
  );
}
